import { useRef } from "react";
import { cn } from "@repo/utils";

type Props = {
  checked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  yesLabel?: string;
  noLabel?: string;
  className?: string;
  containerClassName?: string;
  labelClassName?: string;
};

export const SwitchSelect = ({
  checked = false,
  onCheckedChange,
  disabled = false,
  label,
  yesLabel = "Yes",
  noLabel = "No",
  className,
  containerClassName,
  labelClassName,
}: Props) => {
  const yesRef = useRef<HTMLButtonElement>(null);
  const noRef = useRef<HTMLButtonElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent, _target: "yes" | "no") => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        noRef.current?.focus();
        break;
      case "ArrowLeft":
        e.preventDefault();
        yesRef.current?.focus();
        break;
      case "Enter":
        e.stopPropagation();
        break;
    }
  };

  return (
    <div className={cn("w-full", containerClassName)}>
      {label && (
        <label className={cn("mb-2 block text-sm font-medium", labelClassName)}>{label}</label>
      )}

      <div
        className={cn(
          "inline-flex items-center rounded-lg border border-gray-200 bg-white p-1",
          { "cursor-not-allowed opacity-50": disabled },
          className
        )}
        role="radiogroup"
      >
        <button
          ref={yesRef}
          type="button"
          role="radio"
          aria-checked={checked}
          tabIndex={checked ? 0 : -1}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) onCheckedChange?.(true);
          }}
          onKeyDown={(e) => handleKeyDown(e, "yes")}
          disabled={disabled}
          className={cn(
            "relative flex h-9 min-w-[70px] items-center justify-center rounded-md px-4 text-sm font-medium transition-all focus:z-10",
            "focus:ring-2 focus:ring-blue-500/50 focus:outline-none",
            {
              "bg-blue-100 text-blue-900": checked,
              "bg-transparent text-gray-700 hover:bg-gray-50": !checked,
              "cursor-not-allowed": disabled,
            }
          )}
        >
          {yesLabel}
        </button>

        <button
          ref={noRef}
          type="button"
          role="radio"
          aria-checked={!checked}
          tabIndex={!checked ? 0 : -1}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) onCheckedChange?.(false);
          }}
          onKeyDown={(e) => handleKeyDown(e, "no")}
          disabled={disabled}
          className={cn(
            "relative flex h-9 min-w-[70px] items-center justify-center rounded-md px-4 text-sm font-medium transition-all focus:z-10",
            "focus:ring-2 focus:ring-blue-500/50 focus:outline-none",
            {
              "bg-transparent text-gray-700 hover:bg-gray-50": checked,
              "bg-blue-100 text-blue-900": !checked,
              "cursor-not-allowed": disabled,
            }
          )}
        >
          {noLabel}
        </button>
      </div>
    </div>
  );
};
